import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CompanyProvider, useCompany } from "@/context/CompanyContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import Invoices from "@/pages/Invoices";
import InvoiceEditor from "@/pages/InvoiceEditor";
import RecurringIncome from "@/pages/RecurringIncome";
import Expenses from "@/pages/Expenses";
import Gst from "@/pages/Gst";
import Settings from "@/pages/Settings";
import ChartOfAccounts from "@/pages/ChartOfAccounts";
import Journal from "@/pages/Journal";
import Reports from "@/pages/Reports";
import Admin from "@/pages/Admin";
import Team from "@/pages/Team";
import Account from "@/pages/Account";
import Jobs from "@/pages/Jobs";
import PurchaseOrders from "@/pages/PurchaseOrders";
import JobDetail from "@/pages/JobDetail";
import Time from "@/pages/Time";
import ForcePassword from "@/pages/ForcePassword";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
    </div>
  );
}

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePassword />;
  return (
    <CompanyProvider>
      <CompanyGate />
    </CompanyProvider>
  );
}

function CompanyGate() {
  const { ready } = useCompany();
  if (!ready) return <FullLoader />;
  return <Layout />;
}

function LoginGate() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function AdminRoute() {
  const { user } = useAuth();
  if (!user?.is_admin) return <Navigate to="/" replace />;
  return <Admin />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginGate />} />
            <Route path="/" element={<Protected />}>
              <Route index element={<Dashboard />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="invoices/new" element={<InvoiceEditor />} />
              <Route path="invoices/:id" element={<InvoiceEditor />} />
              <Route path="clients" element={<Clients />} />
              <Route path="recurring" element={<RecurringIncome />} />
              <Route path="expenses" element={<Expenses />} />
              <Route path="jobs" element={<Jobs />} />
              <Route path="purchase-orders" element={<PurchaseOrders />} />
              <Route path="jobs/:id" element={<JobDetail />} />
              <Route path="time" element={<Time />} />
              <Route path="accounts" element={<ChartOfAccounts />} />
              <Route path="journal" element={<Journal />} />
              <Route path="reports" element={<Reports />} />
              <Route path="gst" element={<Gst />} />
              <Route path="settings" element={<Settings />} />
              <Route path="team" element={<Team />} />
              <Route path="account" element={<Account />} />
              <Route path="admin" element={<AdminRoute />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
